import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition, SubTask } from "../common/models";
import { randomUUID } from "../../../utils";
import BorderLinearProgress from "../../../BorderLinearProgress";
import { Typography } from "@mui/material";
import { translateReactNode } from "../../../../utils/translateReactNode";

const TUTORIAL_URLS: Record<string, string> = {
  'en': 'assets/videos/tutorials/Maximum_Phonation_Time_v2.mp4',
  'es-419': 'assets/videos/tutorials/Maximum_Phonation_Time_v2-es-419.mp4'
}

const MAX_DURATION = 20; // Maximum duration in seconds
const MAX_SUCCESS_DURATION = 10; // Maximum duration for success in seconds
const DURATION_GROWTH_RATE = 1.33; // maxDuration is updated to be the 133% of the previous duration
const TIME_TO_DISPLAY_KEEP_GOING = 3;

const Definition: AcousticTaskDefinition = {
  name: "Maximum phonation time (v2)",
  title: "Maximum phonation time",
  description: "This task helps us analyze the way your breathing is connected to your voice.",
  cohort: 'generic',
  type: 'acoustic-task',
  estimatedCompletionTime: '1:30',
  subTaskDefinitions: [{
    name: "Maximum phonation time (v2)-1",
    title: "",
    instructions:  <Typography variant="h5" component='div'>Take a very deep breath, tap on the record button, and hold out “ah” for as long as possible until you completely run out of air. Try to hold it until the progress bar reaches the end of the screen.</Typography>,
    profileName: 'speech'
  }, {
    name: "Maximum phonation time (v2)-2",
    title: "",
    instructions: <>
    <Typography variant="h5" component='div'>Take a very deep breath, tap on the record button, and hold out “ah” for as long as possible until you completely run out of air. Try to hold it until the progress bar reaches the end of the screen.</Typography>
    <Typography variant="h5" component='div' style={{ marginTop: '1rem' }}>Now try to hold out “ah” for even longer.</Typography>
    </>,
    profileName: 'speech'
  }]
};
export const MaximumPhonationTimeDefinition = Definition;

export const MaximumPhonationTime =  ({onCompletion}: {onCompletion: () => void}) => {
  const { t, i18n } = useTranslation("generic");
  const [isRecording, setIsRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentSubTaskIndex, setCurrentSubTaskIndex] = useState(0);
  const [maxDuration, setMaxDuration] = useState<number>(() => MAX_DURATION);
  const [displayEncouragement, setDisplayEncouragement] = useState(false);

  const [task, setTask] = useState<AcousticTask>(() => ({
    ...Definition,
    id: randomUUID(),
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'pending',
    subTaskIndex: 0,
    subTasks: Definition.subTaskDefinitions.map((definition) => ({
      ...definition,
      id: randomUUID(),
      content: <BorderLinearProgress variant="determinate" value={0} />,
      instructions: translateReactNode(definition.instructions, Definition.cohort),
      title: translateReactNode(definition.title, Definition.cohort) as string
    })),
    tutorialUrl: TUTORIAL_URLS[i18n.language] || TUTORIAL_URLS['en']
  }));

  const handleOnCompletion = () => {
    console.log("Audio task completed", task.title);
    onCompletion();
  }

  const handleOnChange = (updatedTask: AcousticTask, currentSubTaskIndex: number) => {
    setCurrentSubTaskIndex(currentSubTaskIndex + 1);
    setProgress(0);

    const updateBasedOnDuration = async (subTask: SubTask) => {
      if (!subTask.audioSource) return;

      const audio = new Audio(subTask.audioSource);
      const duration = await new Promise<number>((resolve) => { // Duration in seconds
        audio.onloadedmetadata = () => resolve(audio.duration);
      });

      if (duration >= MAX_SUCCESS_DURATION) {
        // remove the second subTask
        setTask(prev => {
          prev.subTasks = [ prev.subTasks[0] ];
          return {...prev};
        });
      } else {
        // update the maxDuration
        setMaxDuration(duration * DURATION_GROWTH_RATE);
      }
    }

    if (currentSubTaskIndex === 0) {
      const subTask = updatedTask.subTasks[currentSubTaskIndex];
      if (subTask) {
        updateBasedOnDuration(subTask);
      }
    }
  };

  useEffect(() => {
    if (isRecording) {
      setProgress(0);
      setDisplayEncouragement(false);
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prevProgress + 1;
        });
      }, maxDuration * 10); // 1% of maxDuration in ms

      return () => {
        clearInterval(interval);
      };
    }
  }, [isRecording]);

  useEffect(() => {
    if (task.subTasks.length === 0 || task.subTasks.length <= currentSubTaskIndex) return;

    setTask(prev => {
      const subTask = prev.subTasks[currentSubTaskIndex];
      if (!subTask) return {...prev};

      subTask.content = <div style={{ margin: '1rem 0' }}>
        <BorderLinearProgress variant="determinate" value={progress} sx={{
        '& .MuiLinearProgress-bar': {
          transition: progress === 0 ? 'none' : 'transform 0.5s linear', // Apply 'none' when value is 0
        },
      }} />
        <div style={{ margin: '1rem' }}>
          {displayEncouragement &&
            isRecording && <div className="encouragement encouragement-animate">{t("Keep going!")}</div>
          }
          {displayEncouragement &&
            !isRecording && <div className="encouragement">{t("Thank you!")}</div>
          }
        </div>
      </div>;
      return {...prev}
    });
  }, [progress, currentSubTaskIndex, displayEncouragement, isRecording]);

  useEffect(() => {
    if (progress > (TIME_TO_DISPLAY_KEEP_GOING / maxDuration) * 100) {
      setDisplayEncouragement(true);
    } else {
      setDisplayEncouragement(false);
    }
  }, [progress, maxDuration]);

  return (
    <div>
      <TaskView
        task={task}
        onChange={handleOnChange}
        onIsRecordingChange={setIsRecording}
        onCompletion={handleOnCompletion}
        />
    </div>
  );
}
